import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, MessageCircle, MapPin, Loader2, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { IMG } from "@/lib/imagery";
import Captcha from "@/components/Captcha";
import { useHoneypot } from "@/components/Honeypot";

export default function Contact() {
  const [page, setPage] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState(null);
  const hp = useHoneypot();

  useEffect(() => {
    api.get("/pages/contact").then((r) => setPage(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", {
        name, email, message,
        captcha_token: captcha?.token, captcha_answer: captcha?.answer,
        ...hp.values(),
      });
      toast.success("Message received. We reply within 12 hours.");
      setName(""); setEmail(""); setMessage("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally {
      setBusy(false);
    }
  };

  if (!page) return <div className="p-24 text-center text-emerald-100/50">Loading…</div>;

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[color:var(--ink-hairline)]">
        <div className="pointer-events-none absolute inset-0">
          <img
            src={page.hero_image || IMG.charts}
            alt=""
            className="h-full w-full object-cover"
            style={{ filter: "sepia(0.2) saturate(0.55) contrast(1.02) brightness(1.05)" }}
          />
          <div className="absolute inset-0 hero-scrim" />
        </div>
        <div className="grain absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-5 pt-24 pb-16 lg:px-8 lg:pt-32 lg:pb-24">
          <div className="pl-6 lg:pl-10">
            <div className="vline absolute left-5 top-24 h-20 w-[2px] lg:left-8 lg:top-32" />
            <p className="overline mb-6" data-testid="contact-kicker">{page.hero_kicker}</p>
            <h1 data-testid="contact-title" className="max-w-3xl font-editorial text-5xl font-medium leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl">
              {page.hero_title}
            </h1>
          </div>
        </div>
      </section>

      <section className="relative mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] lg:gap-16">
          {/* Form */}
          <div className="card-moss p-8 lg:p-10">
            <p className="overline mb-2">Send a message</p>
            <h2 className="font-editorial text-2xl font-medium text-white sm:text-3xl">
              We answer within <span className="italic text-emerald-300">12 hours</span>.
            </h2>
            <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="contact-form">
              {hp.field}
              <div className="grid gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Name</label>
                  <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="contact-name"
                    className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-4 py-3 text-sm text-white focus:border-emerald-400/70 focus:outline-none focus:ring-2 focus:ring-emerald-400/25" />
                </div>
                <div>
                  <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Email</label>
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="contact-email"
                    className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-4 py-3 text-sm text-white focus:border-emerald-400/70 focus:outline-none focus:ring-2 focus:ring-emerald-400/25" />
                </div>
              </div>
              <div>
                <label className="mb-2 block text-[10px] font-medium uppercase tracking-widest text-emerald-100/60">Message</label>
                <textarea required rows={6} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="contact-message"
                  className="w-full rounded-lg border border-emerald-500/15 bg-[#0d2416]/60 px-4 py-3 text-sm text-white focus:border-emerald-400/70 focus:outline-none focus:ring-2 focus:ring-emerald-400/25" />
              </div>
              <Captcha onChange={setCaptcha} />
              <button disabled={busy} data-testid="contact-submit" className="btn-edge justify-center">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send message
              </button>
            </form>
          </div>

          {/* Contact details */}
          <div className="grid gap-4">
            {page.sections?.map((s, i) => (
              <div key={i} className="card-edge p-6">
                <p className="overline mb-2">{s.heading}</p>
                <p className="text-sm text-emerald-100/70">{s.body}</p>
              </div>
            ))}
            <div className="card-edge p-6">
              <p className="overline mb-3">Channels</p>
              <ul className="grid gap-3 text-sm">
                {page.contact_email && (
                  <li>
                    <a href={`mailto:${page.contact_email}`} className="flex items-center gap-3 text-emerald-100 hover:text-lime-300">
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/10 text-emerald-300"><Mail size={16} /></span>
                      {page.contact_email}
                    </a>
                  </li>
                )}
                {page.contact_telegram && (
                  <li>
                    <a href={page.contact_telegram} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-emerald-100 hover:text-lime-300">
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/10 text-emerald-300"><MessageCircle size={16} /></span>
                      Telegram <ArrowUpRight size={12} />
                    </a>
                  </li>
                )}
                {page.contact_whatsapp && (
                  <li>
                    <a href={page.contact_whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-emerald-100 hover:text-lime-300">
                      <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/10 text-emerald-300"><MessageCircle size={16} /></span>
                      WhatsApp <ArrowUpRight size={12} />
                    </a>
                  </li>
                )}
                {page.contact_location && (
                  <li className="flex items-center gap-3 text-emerald-100/80">
                    <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-500/10 text-emerald-300"><MapPin size={16} /></span>
                    {page.contact_location}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
